import { configurationsService } from 'shared/services/ConfigurationsService'
import { useLanguage } from 'store/locale/hooks/useLanguage'
import { DateTime, DateTimeFormatOptions, LocaleOptions } from 'luxon'
import axios from 'axios'
import type { INewsItem } from './useNews'
import { useStaticSWR } from 'shared/hooks/useStaticSWR'

interface VideoPlateform {
  name: string
  regex: RegExp
  url: (videoCode: string) => string
}

const videoPlatforms: VideoPlateform[] = [
  {
    name: 'vimeo',
    regex: /(https?:\/\/)?(www\.)?(player\.)?vimeo\.com\/([a-z]*\/)*([0-9]{6,11})[?]?.*/,
    url: (videoCode: string) => `https://player.vimeo.com/video/${videoCode}?title=0&byline=0&portrait=0`
  },

  {
    name: 'youtu',
    regex: /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/,
    url: (videoCode: string) => `https://www.youtube.com/embed/${videoCode}`
  }
]

const replaceVideoCode = (url: string | undefined) => {
  if (!url) {
    return ''
  } else {
    let videoURL

    const plateform = videoPlatforms.find((plateform) => url.indexOf(plateform.name) !== -1)
    if (plateform) {
      const splitedURL = url.match(plateform?.regex)
      const videoCode = splitedURL?.pop()
      if (videoCode) {
        videoURL = plateform.url(videoCode)
      }
    }

    return `<iframe src="${videoURL}" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen></iframe>`
  }
}

const replaceContent = (content: string) => {
  const EMBED_VIDEO_REGEX = /<oembed\surl="(https:\/\/[^\s$.?#].[^\s]*)"><\/oembed>/g
  const IMAGE_REGEX = /src="/g
  const LINK_REGEX = /<a\s/g

  return content
    ?.replace(LINK_REGEX, '<a target="_blank" ')
    ?.replace(IMAGE_REGEX, `src="${configurationsService.imageBaseUrl}`)
    .replace(EMBED_VIDEO_REGEX, (_, url) => replaceVideoCode(url))
}

const extractArticle = (data: any, articleURL: string, language: string, luxonLocale: string): INewsItem =>
  data
    ? data
        .map((newsItem) => ({
          id: newsItem.id,
          title: newsItem.title,
          standfirst: newsItem.standfirst,
          publicationState: newsItem.publicationState,
          publicationDate: newsItem.publication_date,
          publicationDateDisplay: DateTime.fromISO(newsItem.publication_date_display, {
            locale: luxonLocale
          })
            .toUTC()
            .toLocaleString(DateTime.DATE_MED as LocaleOptions & DateTimeFormatOptions),
          header: {
            banner: {
              image: {
                urlMobile: configurationsService.getImageFullUrl(newsItem.header_image_mobile?.url),
                urlDesktop: configurationsService.getImageFullUrl(newsItem.header_image?.url)
              }
            }
          },
          isPromoted: newsItem.is_promoted,
          promotedHeaderImage: configurationsService.getImageFullUrl(newsItem.promoted_header?.url),
          thumbnail: configurationsService.getImageFullUrl(newsItem.thumbnail?.url),
          tags: newsItem.tags?.map((tag) => ({
            name: tag[`name_${language}`],
            code: tag.code
          })),
          content: replaceContent(newsItem.content),
          url: `actualites/article/${articleURL}`,
          translatedLink: newsItem.translated_news_item
        }))
        .find((_, index) => index === 0)
    : ({} as INewsItem)

const fetcher = (url, articleURL) => {
  if (!articleURL) {
    return []
  }
  return axios.get(url, { params: { url: articleURL } }).then(({ data }) => data)
}

export const useArticle = (articleURL: string) => {
  const { language, languageLuxonLocale } = useLanguage()
  const { data } = useStaticSWR({ key: [configurationsService.articleEndpoint, articleURL], fetcher })

  const article: INewsItem = extractArticle(data, articleURL, language, languageLuxonLocale)

  return {
    article,
    isLoading: !data
  }
}
